import { useState, useEffect } from "react";
import { supabase } from "../../../lib/supabase";

interface StaffRow {
  id?: number;
  m_code: string;
  full_name: string;
  phone: string;
  email: string;
  password: string;
  status: string;
  created_at?: string;
}

const SuperAdminStaffManager = () => {
  const [staffList, setStaffList] = useState<StaffRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editStaff, setEditStaff] = useState<StaffRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<StaffRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [form, setForm] = useState({ full_name: "", phone: "", email: "" });
  const [formError, setFormError] = useState("");

  const fetchStaff = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("staff")
      .select("*")
      .order("created_at", { ascending: true });
    setStaffList((data as StaffRow[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { fetchStaff(); }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const generateMCode = (existingStaff: StaffRow[]) => {
    const staffCodes = existingStaff
      .filter(s => s.m_code.startsWith("M1-12-"))
      .map(s => parseInt(s.m_code.replace("M1-12-", ""), 10))
      .filter(n => !isNaN(n));
    const nextNum = staffCodes.length > 0 ? Math.max(...staffCodes) + 1 : 1;
    return `M1-12-${String(nextNum).padStart(3, "0")}`;
  };

  const generateDefaultPassword = (phone: string, name: string) => {
    const nameParts = name.trim().split(" ");
    const lastName = nameParts[nameParts.length - 1];
    const suffix = lastName.substring(0, 3);
    return `${phone}${suffix}`;
  };

  const handleAddStaff = async () => {
    setFormError("");
    if (!form.full_name.trim() || !form.phone.trim()) {
      setFormError("Vui lòng nhập đầy đủ họ tên và số điện thoại.");
      return;
    }
    setSaving(true);
    const mCode = generateMCode(staffList);
    const password = generateDefaultPassword(form.phone, form.full_name);
    const { error } = await supabase.from("staff").insert({
      m_code: mCode,
      full_name: form.full_name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      password,
      status: "active",
    });
    setSaving(false);
    if (error) { setFormError(error.message); return; }
    setShowModal(false);
    setForm({ full_name: "", phone: "", email: "" });
    showToast(`Đã thêm nhân viên ${form.full_name} với mã ${mCode}`);
    fetchStaff();
  };

  const handleUpdatePassword = async (staff: StaffRow, newPassword: string) => {
    await supabase.from("staff").update({ password: newPassword, updated_at: new Date().toISOString() }).eq("m_code", staff.m_code);
    showToast("Đã cập nhật mật khẩu!");
    fetchStaff();
  };

  const handleToggleStatus = async (staff: StaffRow) => {
    const newStatus = staff.status === "active" ? "inactive" : "active";
    await supabase.from("staff").update({ status: newStatus }).eq("m_code", staff.m_code);
    showToast(newStatus === "active" ? "Đã kích hoạt tài khoản!" : "Đã vô hiệu hóa tài khoản!");
    fetchStaff();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    if (deleteTarget.m_code === "M1-11-000") { showToast("Không thể xóa tài khoản Super Admin!"); setDeleteTarget(null); return; }
    setDeleting(true);
    await supabase.from("staff").delete().eq("m_code", deleteTarget.m_code);
    setDeleting(false);
    setDeleteTarget(null);
    showToast("Đã xóa nhân viên!");
    fetchStaff();
  };

  const toggleShowPassword = (code: string) => {
    setShowPasswords(prev => ({ ...prev, [code]: !prev[code] }));
  };

  const superAdmin = staffList.find(s => s.m_code === "M1-11-000");
  const regularStaff = staffList.filter(s => s.m_code !== "M1-11-000");

  return (
    <div>
      {toast && (
        <div className="fixed top-4 right-4 z-[60] bg-green-600 text-white text-sm px-4 py-2.5 rounded-xl shadow-lg">
          <i className="ri-checkbox-circle-line mr-2" />{toast}
        </div>
      )}

      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-800">Quản Lý Nhân Viên (Super Admin)</h1>
        <p className="text-sm text-gray-500 mt-1">Thêm, quản lý nhân viên với hệ thống M-Code. Chỉ Super Admin (M1-11-000) mới có quyền truy cập.</p>
      </div>

      {/* M-Code System Info */}
      <div className="bg-[#EDF2FB] border border-[#004A99]/20 rounded-2xl p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <i className="ri-information-line text-[#004A99] text-lg" />
          <h4 className="font-bold text-[#004A99] text-sm">Hệ thống M-Code</h4>
        </div>
        <div className="grid grid-cols-3 gap-4 text-xs text-[#004A99]/80">
          <div className="bg-white rounded-xl p-3">
            <p className="font-bold text-[#004A99] mb-1">M1-11-000</p>
            <p>Super Admin (Quản trị viên cấp cao nhất)</p>
          </div>
          <div className="bg-white rounded-xl p-3">
            <p className="font-bold text-[#004A99] mb-1">M1-12-XXX</p>
            <p>Nhân viên (Staff) — XXX là số thứ tự 001, 002...</p>
          </div>
          <div className="bg-white rounded-xl p-3">
            <p className="font-bold text-[#004A99] mb-1">Mật khẩu mặc định</p>
            <p>[Số điện thoại] + [3 ký tự cuối tên]</p>
            <p className="text-gray-400 mt-1">VD: 0901234567Thu</p>
          </div>
        </div>
      </div>

      {/* Super Admin Card */}
      {superAdmin && (
        <div className="mb-6">
          <h3 className="text-sm font-bold text-gray-600 mb-3 flex items-center gap-2">
            <i className="ri-shield-star-line text-amber-500" />
            Tài khoản Super Admin
          </h3>
          <div className="bg-gradient-to-r from-[#001428] to-[#004A99] rounded-2xl p-5 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <i className="ri-shield-star-line text-amber-400 text-xl" />
                </div>
                <div>
                  <p className="font-bold text-lg">{superAdmin.full_name}</p>
                  <p className="text-white/70 text-sm font-mono">{superAdmin.m_code}</p>
                  <p className="text-white/60 text-xs mt-0.5">{superAdmin.email || "—"}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-white/60 text-xs mb-1">Mật khẩu hiện tại</p>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm bg-white/10 px-3 py-1.5 rounded-lg">
                    {showPasswords[superAdmin.m_code] ? superAdmin.password : "••••••••••"}
                  </span>
                  <button
                    onClick={() => toggleShowPassword(superAdmin.m_code)}
                    className="w-8 h-8 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-lg cursor-pointer transition-colors"
                  >
                    <i className={`${showPasswords[superAdmin.m_code] ? "ri-eye-off-line" : "ri-eye-line"} text-sm`} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Staff List */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-gray-600 flex items-center gap-2">
          <i className="ri-team-line text-[#004A99]" />
          Danh sách Nhân viên ({regularStaff.length})
        </h3>
        <button
          onClick={() => { setShowModal(true); setForm({ full_name: "", phone: "", email: "" }); setFormError(""); }}
          className="flex items-center gap-2 px-4 py-2 bg-[#004A99] hover:bg-[#003a7a] text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-user-add-line" />
          Thêm nhân viên mới
        </button>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl border border-gray-200 py-12 text-center">
          <i className="ri-loader-4-line text-2xl text-[#004A99] animate-spin block mb-2" />
          <p className="text-sm text-gray-400">Đang tải...</p>
        </div>
      ) : regularStaff.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 py-12 text-center text-gray-400 text-sm">
          <i className="ri-user-line text-3xl block mb-2" />
          Chưa có nhân viên nào. Hãy thêm nhân viên đầu tiên!
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">M-Code</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Họ tên</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Số điện thoại</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Mật khẩu hiện tại</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Trạng thái</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {regularStaff.map((staff) => (
                <StaffRow
                  key={staff.m_code}
                  staff={staff}
                  showPassword={showPasswords[staff.m_code] ?? false}
                  onTogglePassword={() => toggleShowPassword(staff.m_code)}
                  onUpdatePassword={(pwd) => handleUpdatePassword(staff, pwd)}
                  onToggleStatus={() => handleToggleStatus(staff)}
                  onDelete={() => setDeleteTarget(staff)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Staff Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold text-gray-800">Thêm nhân viên mới</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>

            <div className="bg-[#EDF2FB] rounded-xl p-4 mb-5 text-xs text-[#004A99]">
              <p className="font-semibold mb-1">Hệ thống sẽ tự động:</p>
              <ul className="space-y-1 list-disc pl-4">
                <li>Cấp mã M-Code theo thứ tự (M1-12-001, M1-12-002...)</li>
                <li>Tạo mật khẩu mặc định: [SĐT] + [3 ký tự cuối tên]</li>
              </ul>
            </div>

            {formError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2 mb-4">
                {formError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Họ và tên đầy đủ *</label>
                <input
                  type="text"
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  placeholder="Nguyễn Thị Thu"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#004A99]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Số điện thoại *</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="0901234567"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#004A99]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Email (tùy chọn)</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="email@bizwell.vn"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#004A99]"
                />
              </div>

              {form.full_name && form.phone && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-xs">
                  <p className="font-semibold text-green-800 mb-1">Thông tin sẽ được tạo:</p>
                  <p className="text-green-700">M-Code: <strong className="font-mono">{generateMCodePreview(staffList)}</strong></p>
                  <p className="text-green-700">Mật khẩu: <strong className="font-mono">{generateDefaultPasswordPreview(form.phone, form.full_name)}</strong></p>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              >
                Hủy
              </button>
              <button
                onClick={handleAddStaff}
                disabled={saving}
                className="flex-1 py-2.5 text-sm font-semibold text-white bg-[#004A99] hover:bg-[#003a7a] rounded-xl cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving && <i className="ri-loader-4-line animate-spin" />}
                Thêm nhân viên
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
              <i className="ri-delete-bin-line text-red-600 text-xl" />
            </div>
            <h3 className="font-bold text-gray-800 mb-2">Xác nhận xóa</h3>
            <p className="text-sm text-gray-600 mb-5">
              Bạn có chắc muốn xóa nhân viên <strong>"{deleteTarget.full_name}"</strong> ({deleteTarget.m_code})?
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap">Hủy</button>
              <button onClick={handleDelete} disabled={deleting} className="flex-1 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-1">
                {deleting && <i className="ri-loader-4-line animate-spin" />}
                Xóa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper functions for preview
const generateMCodePreview = (staffList: StaffRow[]) => {
  const staffCodes = staffList
    .filter(s => s.m_code.startsWith("M1-12-"))
    .map(s => parseInt(s.m_code.replace("M1-12-", ""), 10))
    .filter(n => !isNaN(n));
  const nextNum = staffCodes.length > 0 ? Math.max(...staffCodes) + 1 : 1;
  return `M1-12-${String(nextNum).padStart(3, "0")}`;
};

const generateDefaultPasswordPreview = (phone: string, name: string) => {
  const nameParts = name.trim().split(" ");
  const lastName = nameParts[nameParts.length - 1];
  const suffix = lastName.substring(0, 3);
  return `${phone}${suffix}`;
};

// Staff Row Component
interface StaffRowProps {
  staff: StaffRow;
  showPassword: boolean;
  onTogglePassword: () => void;
  onUpdatePassword: (pwd: string) => void;
  onToggleStatus: () => void;
  onDelete: () => void;
}

const StaffRow = ({ staff, showPassword, onTogglePassword, onUpdatePassword, onToggleStatus, onDelete }: StaffRowProps) => {
  const [editingPwd, setEditingPwd] = useState(false);
  const [newPwd, setNewPwd] = useState("");

  const handleSavePwd = () => {
    if (!newPwd.trim()) return;
    onUpdatePassword(newPwd.trim());
    setEditingPwd(false);
    setNewPwd("");
  };

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-5 py-4">
        <code className="text-xs font-mono text-[#004A99] bg-[#EDF2FB] px-2 py-1 rounded whitespace-nowrap">
          {staff.m_code}
        </code>
      </td>
      <td className="px-4 py-4">
        <p className="text-sm font-semibold text-gray-900">{staff.full_name}</p>
        <p className="text-xs text-gray-400">{staff.created_at?.slice(0, 10)}</p>
      </td>
      <td className="px-4 py-4 text-sm text-gray-600 whitespace-nowrap">{staff.phone}</td>
      <td className="px-4 py-4 text-sm text-gray-600">{staff.email || "—"}</td>
      <td className="px-4 py-4">
        {editingPwd ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              placeholder="Mật khẩu mới..."
              className="border border-gray-200 rounded-lg px-2 py-1 text-xs outline-none focus:border-[#004A99] w-32 font-mono"
              autoFocus
            />
            <button onClick={handleSavePwd} className="text-xs text-green-600 font-semibold cursor-pointer whitespace-nowrap hover:underline">Lưu</button>
            <button onClick={() => { setEditingPwd(false); setNewPwd(""); }} className="text-xs text-gray-400 cursor-pointer whitespace-nowrap hover:underline">Hủy</button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded whitespace-nowrap">
              {showPassword ? staff.password : "••••••••"}
            </span>
            <button onClick={onTogglePassword} className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
              <i className={`${showPassword ? "ri-eye-off-line" : "ri-eye-line"} text-xs`} />
            </button>
            <button onClick={() => setEditingPwd(true)} className="w-6 h-6 flex items-center justify-center text-[#004A99] hover:bg-[#EDF2FB] rounded cursor-pointer">
              <i className="ri-edit-line text-xs" />
            </button>
          </div>
        )}
      </td>
      <td className="px-4 py-4">
        <button
          onClick={onToggleStatus}
          className={`text-xs px-2.5 py-1 rounded-full font-semibold cursor-pointer whitespace-nowrap transition-colors ${
            staff.status === "active"
              ? "bg-green-100 text-green-700 hover:bg-green-200"
              : "bg-gray-100 text-gray-500 hover:bg-gray-200"
          }`}
        >
          {staff.status === "active" ? "Hoạt động" : "Vô hiệu"}
        </button>
      </td>
      <td className="px-4 py-4">
        <button
          onClick={onDelete}
          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-400 transition-colors cursor-pointer"
        >
          <i className="ri-delete-bin-line text-sm" />
        </button>
      </td>
    </tr>
  );
};

export default SuperAdminStaffManager;
